import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Package, Search, Plus, Trash2, Save, AlertCircle, CheckCircle, User, Camera, Image, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detectar carrier automáticamente por el formato del tracking
const detectCarrier = (tracking) => {
  if (!tracking) return '';
  const t = tracking.toUpperCase();
  if (t.match(/^TBA/)) return 'Amazon';
  if (t.match(/^1Z/)) return 'UPS';
  if (t.match(/^[0-9]{12,22}$/)) return 'FedEx';
  if (t.match(/^94/)) return 'USPS';
  if (t.match(/^[0-9]{10}$/)) return 'DHL';
  return 'Otro';
};

const CreatePackage = ({ token, clients = [], onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  
  // Form data for multiple packages
  const [packages, setPackages] = useState([{
    tracking_number: '',
    package_number: '',
    carrier: '',
    description: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    pieces: 1,
    declared_value: '',
    photo_package: null,
    photo_label: null,
    photo_package_preview: null,
    photo_label_preview: null
  }]);
  
  const [selectedClient, setSelectedClient] = useState(null);
  const [ubicacion, setUbicacion] = useState('miami_warehouse');

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, axiosConfig);
      setAllClients(response.data);
    } catch (err) {
      // Use passed clients if API fails
      if (clients.length > 0) {
        setAllClients(clients);
      }
    }
  };

  // Filter clients based on search
  useEffect(() => {
    if (clientSearch.length > 0) {
      const search = clientSearch.toLowerCase();
      const filtered = allClients.filter(c => 
        c.full_name?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search) ||
        c.locker_number?.toLowerCase().includes(search) ||
        c.locker_code?.toLowerCase().includes(search) ||
        c.locker_id?.toLowerCase().includes(search)
      );
      setFilteredClients(filtered.slice(0, 8));
      setShowClientDropdown(true);
    } else {
      setFilteredClients([]);
      setShowClientDropdown(false);
    }
  }, [clientSearch, allClients]);

  const handlePackageChange = (index, field, value) => {
    const updated = [...packages];
    updated[index][field] = value;
    
    // Auto-detect carrier when tracking changes
    if (field === 'tracking_number') {
      const carrier = detectCarrier(value);
      if (carrier) {
        updated[index].carrier = carrier;
      }
    }
    
    setPackages(updated);
  };

  const addPackage = () => {
    setPackages([...packages, {
      tracking_number: '',
      package_number: '',
      carrier: '',
      description: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      pieces: 1,
      declared_value: '',
      photo_package: null,
      photo_label: null,
      photo_package_preview: null,
      photo_label_preview: null
    }]);
  };

  const removePackage = (index) => {
    if (packages.length > 1) {
      setPackages(packages.filter((_, i) => i !== index));
    }
  };

  const handlePhotoChange = (index, photoType, file) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten archivos de imagen');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo excede el tamaño máximo de 5MB');
        return;
      }
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      const newPackages = [...packages];
      newPackages[index][`photo_${photoType}`] = file;
      newPackages[index][`photo_${photoType}_preview`] = previewUrl;
      setPackages(newPackages);
    }
  };

  const removePhoto = (index, photoType) => {
    const newPackages = [...packages];
    // Revoke the preview URL to free memory
    if (newPackages[index][`photo_${photoType}_preview`]) {
      URL.revokeObjectURL(newPackages[index][`photo_${photoType}_preview`]);
    }
    newPackages[index][`photo_${photoType}`] = null;
    newPackages[index][`photo_${photoType}_preview`] = null;
    setPackages(newPackages);
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setClientSearch(client.full_name + ' - ' + (client.locker_number || client.locker_code || client.locker_id || ''));
    setShowClientDropdown(false);
  };

  const calculateVolume = (pkg) => {
    const l = parseFloat(pkg.length) || 0;
    const w = parseFloat(pkg.width) || 0;
    const h = parseFloat(pkg.height) || 0;
    return ((l * w * h) / 1728).toFixed(2); // Convert to cubic feet
  };

  const getTotals = () => {
    return packages.reduce((acc, pkg) => {
      return {
        pieces: acc.pieces + (parseInt(pkg.pieces) || 1),
        weight: acc.weight + (parseFloat(pkg.weight) || 0),
        value: acc.value + (parseFloat(pkg.declared_value) || 0),
        volume: acc.volume + parseFloat(calculateVolume(pkg))
      };
    }, { pieces: 0, weight: 0, value: 0, volume: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClient) {
      setError('Debes seleccionar un cliente');
      return;
    }

    // Validate at least one package with tracking
    const validPackages = packages.filter(p => p.tracking_number.trim());
    if (validPackages.length === 0) {
      setError('Debes agregar al menos un paquete con número de tracking');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const createdPackages = [];
      
      for (const pkg of validPackages) {
        const trackingData = {
          tracking_number: pkg.tracking_number.trim(),
          package_number: pkg.package_number?.trim() || null,
          client_id: selectedClient.id,
          carrier: pkg.carrier || 'Otro',
          description: pkg.description,
          weight: parseFloat(pkg.weight) || 0,
          length: parseFloat(pkg.length) || 0,
          width: parseFloat(pkg.width) || 0,
          height: parseFloat(pkg.height) || 0,
          pieces: parseInt(pkg.pieces) || 1,
          declared_value: parseFloat(pkg.declared_value) || 0,
          status: 'received',
          location: ubicacion
        };

        const response = await axios.post(`${API}/admin/trackings`, trackingData, axiosConfig);
        const createdTracking = response.data;
        
        // Upload photos if they exist
        if (pkg.photo_package) {
          const formData = new FormData();
          formData.append('file', pkg.photo_package);
          await axios.post(
            `${API}/admin/trackings/${createdTracking.id || createdTracking.tracking_id}/upload-photo?photo_type=package`,
            formData,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
        }
        
        if (pkg.photo_label) {
          const formData = new FormData();
          formData.append('file', pkg.photo_label);
          await axios.post(
            `${API}/admin/trackings/${createdTracking.id || createdTracking.tracking_id}/upload-photo?photo_type=label`,
            formData,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
        }
        
        // Send the "package received" email AFTER photos are uploaded so it
        // includes the package number and photo attachments.
        try {
          await axios.post(
            `${API}/admin/trackings/${createdTracking.id || createdTracking.tracking_id}/notify-received`,
            {},
            axiosConfig
          );
        } catch (notifyErr) {
          // Non-blocking: package is already created even if the email fails
        }
        
        createdPackages.push(createdTracking);
      }

      setSuccess(`✅ Se crearon ${createdPackages.length} paquete(s) exitosamente para ${selectedClient.full_name}`);
      
      // Reset form
      setPackages([{
        tracking_number: '',
        package_number: '',
        carrier: '',
        description: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        pieces: 1,
        declared_value: '',
        photo_package: null,
        photo_label: null,
        photo_package_preview: null,
        photo_label_preview: null
      }]);
      setSelectedClient(null);
      setClientSearch('');
      
      if (onSuccess) onSuccess();
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear los paquetes');
    } finally {
      setLoading(false);
    }
  };

  const totals = getTotals();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Package className="text-blue-600" />
            Crear Paquete(s)
          </CardTitle>
          <CardDescription>
            Registra paquetes recibidos en bodega y asígnalos a casilleros de clientes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Success/Error Messages */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-green-800">{success}</span>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
                <AlertCircle className="text-red-600" size={20} />
                <span className="text-red-800">{error}</span>
              </div>
            )}

            {/* Cliente Selection */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <User size={18} />
                Asignar a Cliente
              </h3>
              
              <div className="relative">
                <Label className="text-blue-700">Buscar por nombre, email o código de casillero</Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      placeholder="Ej: TDA000100, juan@email.com, Juan Pérez..."
                      className="pl-10"
                    />
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                  </div>
                </div>
                
                {/* Client Dropdown */}
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => selectClient(client)}
                        className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                      >
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                          {client.full_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{client.full_name}</p>
                          <p className="text-sm text-gray-500">{client.email}</p>
                        </div>
                        <div className="text-right">
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm font-mono">
                            {client.locker_number || client.locker_code || client.locker_id || 'Sin código'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected Client Display */}
              {selectedClient && (
                <div className="mt-3 bg-white border border-blue-300 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-green-600" size={20} />
                    <div>
                      <p className="font-semibold">{selectedClient.full_name}</p>
                      <p className="text-sm text-gray-500">{selectedClient.email}</p>
                    </div>
                  </div>
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded font-mono font-bold">
                    {selectedClient.locker_number || selectedClient.locker_code || selectedClient.locker_id || 'Sin casillero'}
                  </span>
                </div>
              )}
            </div>

            {/* Ubicación */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Ubicación del Paquete</Label>
                <Select value={ubicacion} onValueChange={setUbicacion}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="miami_warehouse">Bodega Miami</SelectItem>
                    <SelectItem value="in_transit">En Tránsito</SelectItem>
                    <SelectItem value="customs">En Aduana</SelectItem>
                    <SelectItem value="venezuela_warehouse">Bodega Venezuela</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Package List */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-3 flex justify-between items-center">
                <h3 className="font-semibold text-gray-700">Listado de Paquetes</h3>
                <Button type="button" variant="outline" size="sm" onClick={addPackage} className="gap-1">
                  <Plus size={16} />
                  Agregar Paquete
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Tracking *</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Nro. Paquete</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Carrier</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">Descripción</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-20">Peso (lb)</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-16">L</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-16">W</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-16">H</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-16">Pzas</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-20">Valor $</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-16">Vol CF</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 w-32">
                        <div className="flex items-center gap-1">
                          <Camera size={14} />
                          Fotos
                        </div>
                      </th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {packages.map((pkg, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <Input
                            value={pkg.tracking_number}
                            onChange={(e) => handlePackageChange(index, 'tracking_number', e.target.value)}
                            placeholder="TBA..."
                            className="font-mono text-xs h-8"
                            data-testid={`tracking-number-${index}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={pkg.package_number}
                            onChange={(e) => handlePackageChange(index, 'package_number', e.target.value)}
                            placeholder="Nro. Proveedor"
                            className="font-mono text-xs h-8"
                            data-testid={`package-number-${index}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Select 
                            value={pkg.carrier} 
                            onValueChange={(v) => handlePackageChange(index, 'carrier', v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Carrier" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Amazon">Amazon</SelectItem>
                              <SelectItem value="FedEx">FedEx</SelectItem>
                              <SelectItem value="UPS">UPS</SelectItem>
                              <SelectItem value="USPS">USPS</SelectItem>
                              <SelectItem value="DHL">DHL</SelectItem>
                              <SelectItem value="Otro">Otro</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={pkg.description}
                            onChange={(e) => handlePackageChange(index, 'description', e.target.value)}
                            placeholder="Descripción..."
                            className="text-xs h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={pkg.weight}
                            onChange={(e) => handlePackageChange(index, 'weight', e.target.value)}
                            className="text-xs h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={pkg.length}
                            onChange={(e) => handlePackageChange(index, 'length', e.target.value)}
                            className="text-xs h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={pkg.width}
                            onChange={(e) => handlePackageChange(index, 'width', e.target.value)}
                            className="text-xs h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={pkg.height}
                            onChange={(e) => handlePackageChange(index, 'height', e.target.value)}
                            className="text-xs h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            value={pkg.pieces}
                            onChange={(e) => handlePackageChange(index, 'pieces', e.target.value)}
                            className="text-xs h-8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={pkg.declared_value}
                            onChange={(e) => handlePackageChange(index, 'declared_value', e.target.value)}
                            className="text-xs h-8"
                          />
                        </td>
                        <td className="px-2 py-2 text-xs text-gray-500 text-center">
                          {calculateVolume(pkg)}
                        </td>
                        {/* Photo Upload Column */}
                        <td className="px-2 py-2">
                          <div className="flex gap-1">
                            {/* Package Photo */}
                            <div className="relative">
                              {pkg.photo_package_preview ? (
                                <div className="relative group">
                                  <img 
                                    src={pkg.photo_package_preview} 
                                    alt="Paquete" 
                                    className="w-10 h-10 object-cover rounded border"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(index, 'package')}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ) : (
                                <label className="w-10 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors" title="Foto Paquete">
                                  <Package size={14} className="text-gray-400" />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handlePhotoChange(index, 'package', e.target.files[0])}
                                    data-testid={`photo-package-${index}`}
                                  />
                                </label>
                              )}
                            </div>
                            {/* Label Photo */}
                            <div className="relative">
                              {pkg.photo_label_preview ? (
                                <div className="relative group">
                                  <img 
                                    src={pkg.photo_label_preview} 
                                    alt="Etiqueta" 
                                    className="w-10 h-10 object-cover rounded border"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(index, 'label')}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                              ) : (
                                <label className="w-10 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors" title="Foto Etiqueta">
                                  <Image size={14} className="text-gray-400" />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => handlePhotoChange(index, 'label', e.target.files[0])}
                                    data-testid={`photo-label-${index}`}
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2">
                          {packages.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removePackage(index)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-gray-100 p-3 flex justify-end gap-6 text-sm border-t">
                <div>
                  <span className="text-gray-600">Paquetes:</span>
                  <span className="ml-2 font-bold">{packages.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Piezas:</span>
                  <span className="ml-2 font-bold">{totals.pieces}</span>
                </div>
                <div>
                  <span className="text-gray-600">Peso Total:</span>
                  <span className="ml-2 font-bold">{totals.weight.toFixed(2)} lb</span>
                </div>
                <div>
                  <span className="text-gray-600">Volumen:</span>
                  <span className="ml-2 font-bold">{totals.volume.toFixed(2)} CF</span>
                </div>
                <div>
                  <span className="text-gray-600">Valor:</span>
                  <span className="ml-2 font-bold">${totals.value.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPackages([{
                    tracking_number: '',
                    package_number: '',
                    carrier: '',
                    description: '',
                    weight: '',
                    length: '',
                    width: '',
                    height: '',
                    pieces: 1,
                    declared_value: '',
                    photo_package: null,
                    photo_label: null,
                    photo_package_preview: null,
                    photo_label_preview: null
                  }]);
                  setSelectedClient(null);
                  setClientSearch('');
                  setSuccess(null);
                  setError(null);
                }}
              >
                Limpiar
              </Button>
              <Button type="submit" disabled={loading} className="gap-2">
                {loading ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <Save size={18} />
                    Crear {packages.length} Paquete(s)
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatePackage;
